/**
 * Chart Configuration Utilities
 * Shared configurations for Chart.js across all reports
 */

// Professional B2B color palette
export const CHART_COLORS = {
    primary: '#2563EB',
    secondary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#06B6D4',
    dark: '#1E293B',
    light: '#F1F5F9',

    // Status colors
    pending: '#FCD34D',
    active: '#6366F1',
    completed: '#10B981',
    cancelled: '#EF4444',

    // Gradient colors for charts
    gradients: [
        '#1E3A8A',
        '#2563EB',
        '#3B82F6',
        '#60A5FA',
        '#93C5FD',
        '#DBEAFE'
    ],

    // Multi-series colors
    series: [
        '#2563EB',
        '#10B981',
        '#F59E0B',
        '#EF4444',
        '#8B5CF6',
        '#EC4899',
        '#06B6D4',
        '#84CC16'
    ]
};

// Default chart options
export const getDefaultChartOptions = (type = 'bar') => {
    const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    padding: 15,
                    usePointStyle: true,
                    font: {
                        size: 12,
                        weight: '500',
                        family: "'Inter', 'Segoe UI', sans-serif"
                    },
                    color: '#64748B'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleColor: '#F1F5F9',
                bodyColor: '#F1F5F9',
                borderColor: '#334155',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                titleFont: {
                    size: 13,
                    weight: '600'
                },
                bodyFont: {
                    size: 12
                },
                displayColors: true,
                boxPadding: 6
            }
        }
    };

    if (type === 'bar' || type === 'line') {
        return {
            ...baseOptions,
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        color: '#E2E8F0',
                        drawBorder: false
                    },
                    ticks: {
                        color: '#64748B',
                        font: {
                            size: 11,
                            weight: '500'
                        },
                        padding: 8
                    }
                },
                x: {
                    grid: {
                        display: false,
                        drawBorder: false
                    },
                    ticks: {
                        color: '#64748B',
                        font: {
                            size: 11,
                            weight: '500'
                        },
                        padding: 8
                    }
                }
            }
        };
    }

    if (type === 'doughnut' || type === 'pie') {
        return {
            ...baseOptions,
            cutout: type === 'doughnut' ? '70%' : '0%',
            plugins: {
                ...baseOptions.plugins,
                legend: {
                    ...baseOptions.plugins.legend,
                    position: 'right'
                }
            }
        };
    }

    return baseOptions;
};

// Format currency for tooltips
export const formatCurrency = (value, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(value);
};

// Format number with separators
export const formatNumber = (value) => {
    return new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 1
    }).format(value);
};

// Format percentage
export const formatPercentage = (value, decimals = 1) => {
    return `${value.toFixed(decimals)}%`;
};

// Custom tooltip callbacks
export const tooltipCallbacks = {
    currency: (context) => {
        const label = context.dataset.label || '';
        const value = context.parsed.y || context.parsed;
        return `${label}: ${formatCurrency(value)}`;
    },

    percentage: (context) => {
        const label = context.dataset.label || '';
        const value = context.parsed.y || context.parsed;
        return `${label}: ${formatPercentage(value)}`;
    },

    number: (context) => {
        const label = context.dataset.label || '';
        const value = context.parsed.y || context.parsed;
        return `${label}: ${formatNumber(value)}`;
    }
};

// Chart data labels plugin configuration
export const getDataLabelsConfig = (type = 'value') => {
    const baseConfig = {
        color: '#475569', // Dark slate for better readability
        font: {
            weight: '600',
            size: 11
        },
        anchor: 'end',
        align: 'top',
        offset: 4,
        display: (context) => {
            // Hide small values to avoid clutter
            return context.dataset.data[context.dataIndex] > 0;
        }
    };

    if (type === 'currency') {
        return {
            ...baseConfig,
            formatter: (value) => formatCurrency(value)
        };
    }

    if (type === 'percentage') {
        return {
            ...baseConfig,
            formatter: (value) => formatPercentage(value)
        };
    }

    return {
        ...baseConfig,
        formatter: (value) => formatNumber(value)
    };
};

// Responsive breakpoints
export const CHART_BREAKPOINTS = {
    mobile: 576,
    tablet: 768,
    desktop: 1024,
    wide: 1440
};

// Get chart height based on screen size
export const getResponsiveHeight = (baseHeight = 300) => {
    const width = window.innerWidth;

    if (width < CHART_BREAKPOINTS.mobile) {
        return baseHeight * 0.8;
    }
    if (width < CHART_BREAKPOINTS.tablet) {
        return baseHeight * 0.9;
    }
    return baseHeight;
};
